/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, mergeMap, skip, take } from 'rxjs/operators';
import { OrganisationSharedService } from '../../../services/shared/organisation-shared/organisation-shared.service';
import { HelperService } from '../../../services/helper/helper.service';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { OrganisationStore } from '../../../store/organisation/component-store/organisation.store';
import { OrganisationInterface } from 'src/app/models/interface/organisation/organisation.interface';
import { PaginationData, PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';
import { Store } from '@ngrx/store';
import { StudyActionTypes } from 'src/app/store/study/study.action';

@Component({
  selector: 'app-organisation-study',
  templateUrl: './organisation-study.component.html',
  styleUrls: ['./organisation-study.component.scss'],
  providers: [OrganisationStore],
})
export class OrganisationStudyComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  public organisationId: number;
  public isLoading$: Observable<boolean>;

  // Filter
  public filter = { searchFilter: '' };

  public studies$: Observable<StudyInterface[]>;

  public searchTextSubject = new BehaviorSubject<string>('');

  public pagedStudies$: Observable<StudyInterface[]>;

  public organisations: OrganisationInterface[];

  private initialStudies: StudyInterface[];

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private studiesSubject = new BehaviorSubject<StudyInterface[]>([]);
  private pagedStudiesSubject = new BehaviorSubject<StudyInterface[]>([]);
  private studiesDataSubject = new BehaviorSubject<PaginationData<StudyInterface>>({ items: [] });

  private organisationStudies$: Observable<StudyInterface[]>;
  studiesData$: Observable<PaginationData<StudyInterface>>;

  private groups$: Observable<StudyInterface[]>;

  private subscriptions: Subscription[] = [];

  constructor(
    private sharedService: OrganisationSharedService,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private translateService: TranslateService,
    private organisationStore: OrganisationStore,
    private store: Store<{ collabGroups: StudyInterface[] }>,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.studies$ = this.studiesSubject.asObservable();
    this.pagedStudies$ = this.pagedStudiesSubject.asObservable();
    this.studiesData$ = this.studiesDataSubject.asObservable();
    this.organisationStudies$ = this.organisationStore.organisationStudies$;
    this.groups$ = this.store.select('collabGroups');
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      // Collaborating studies are needed in app-card-study.
      // Load if they weren't loaded already, which should only happen on page reload.
      this.groups$.pipe(
        filter((studies) => !Array.isArray(studies) || studies.length === 0),
      ).subscribe({
        next: () => {
          this.store.dispatch({
            type: StudyActionTypes.getCollaboratingStudiesType,
            payload: {
              type: 'study,organisation',
              include: 'owners,roles',
            },
          });
        },
      }),
    );

    this.subscriptions.push(
      this.sharedService.sourceOrganisation$.subscribe((value: OrganisationInterface) => {
        // Due to async service check whether value is null or valid id
        if (value === null || value === undefined) {
          this.isLoadingSubject.next(true);
        } else {
          this.organisations = [value];
          this.organisationId = value.id;
          this.applyStudyFilter();
        }
      })
    );

    this.subscriptions.push(
      this.translateService.onLangChange.subscribe((_event: LangChangeEvent) => {
        this.applyStudyFilter();
      }),
    );
  }

  public applyStudyFilter(setFirst?: boolean): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadStudyList(setFirst).subscribe(() => {
        this.isLoadingSubject.next(false);
      }),
    );
  }

  public reloadStudyList(setFirst?: boolean): Observable<boolean> {
    const pageIndex = this.paginator?.paginator ? this.paginator?.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;
    this.organisationStore.getOrganisationStudies({
      organisationId: this.organisationId,
      include: 'collaborators',
    });
    return this.organisationStudies$.pipe(
      skip(1),
      take(1),
      mergeMap((results) => {
        this.initialStudies = results;
        return this.search(this.filter.searchFilter).pipe(
          mergeMap(
            () => {
            return this.helper.setPagedContent(
              this.studiesSubject,
              this.pagedStudiesSubject,
              setFirst,
              pageIndex,
              pageSize,
              this.studiesDataSubject,
            )},
          ),
        );
      }),
    );
  }

  public updatePagedStudies(event: StudyInterface[]) {
    if (event) {
      this.pagedStudiesSubject.next(event);
    }
  }

  // Reads search value and runs searchFilter
  private search(name: string): Observable<boolean> {
    this.searchTextSubject.next(name);
    return this.searchTextSubject.pipe(
      distinctUntilChanged(),
      mergeMap(() => {
        const searchTerm = this.filter.searchFilter.trim().toLowerCase();
        this.studiesSubject.next(
          this.helper.filterStudiesBy(this.initialStudies, searchTerm),
        );
        return of(true);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
